import { FC, useState, ReactNode } from "react";
import { Box, Flex, Text, Collapse, Portal } from "@chakra-ui/react";

export interface RetroWindowProps {
  title: string;
  icon?: string;
  children: ReactNode;
  titleBarColor?: string;
  onClose?: () => void;
  showControls?: boolean;
  defaultMaximized?: boolean;
}

export const RetroWindow: FC<RetroWindowProps> = ({
  title,
  icon = "📁",
  children,
  titleBarColor = "linear-gradient(90deg, #000080, #1084d0)",
  onClose,
  showControls = true,
  defaultMaximized = false,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(defaultMaximized);
  const [showCloseDialog, setShowCloseDialog] = useState(false);

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const handleClose = () => {
    setShowCloseDialog(true);
  };

  const confirmClose = () => {
    setShowCloseDialog(false);
    if (onClose) {
      onClose();
    } else {
      // Easter egg: Show BSOD for a moment
      document.body.innerHTML = `
        <div style="
          background: #000080;
          color: white;
          font-family: 'Courier New', monospace;
          padding: 50px;
          height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
        ">
          <h1 style="text-align: center; background: #A0A0A0; color: #000080; padding: 5px;">
            Windows
          </h1>
          <br/><br/>
          <p>A fatal exception 0E has occurred at 0028:C0034B03 in VXD VMOUSE(01) +</p>
          <p>00000B4A. The current application will be terminated.</p>
          <br/>
          <p>* Press any key to terminate the current application.</p>
          <p>* Press CTRL+ALT+DEL to restart your computer. You will</p>
          <p>  lose any unsaved information in all applications.</p>
          <br/><br/>
          <p style="text-align: center;">Press any key to continue _</p>
        </div>
      `;
      document.body.onclick = () => window.location.reload();
      document.body.onkeydown = () => window.location.reload();
    }
  };

  return (
    <>
      <Box
        bg="linear-gradient(180deg, #C0C0C0, #A0A0A0)"
        border="3px outset #FFFFFF"
        boxShadow="4px 4px 0px #404040"
        m={isMaximized ? 0 : 2}
        position={isMaximized ? "fixed" : "relative"}
        top={isMaximized ? 0 : "auto"}
        left={isMaximized ? 0 : "auto"}
        right={isMaximized ? 0 : "auto"}
        bottom={isMaximized ? 0 : "auto"}
        zIndex={isMaximized ? 1000 : 1}
        transition="all 0.2s ease-in-out"
      >
        {/* Window Title Bar */}
        <Flex
          bg={titleBarColor}
          color="white"
          px={3}
          py={2}
          justify="space-between"
          align="center"
          borderBottom="2px solid #808080"
          cursor="grab"
          userSelect="none"
        >
          <Text
            fontFamily="'Comic Sans MS', cursive"
            fontWeight="bold"
            fontSize="md"
          >
            {icon} {title}
          </Text>

          {showControls && (
            <Flex gap={1}>
              {/* Minimize Button */}
              <Box
                w="16px"
                h="16px"
                bg="#C0C0C0"
                border="2px outset white"
                textAlign="center"
                lineHeight="12px"
                fontSize="10px"
                cursor="pointer"
                onClick={handleMinimize}
                _hover={{ bg: "#A0A0A0" }}
                _active={{ border: "2px inset #808080" }}
                title="Minimize"
              >
                _
              </Box>
              {/* Maximize Button */}
              <Box
                w="16px"
                h="16px"
                bg="#C0C0C0"
                border="2px outset white"
                textAlign="center"
                lineHeight="12px"
                fontSize="10px"
                cursor="pointer"
                onClick={handleMaximize}
                _hover={{ bg: "#A0A0A0" }}
                _active={{ border: "2px inset #808080" }}
                title={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? "❐" : "□"}
              </Box>
              {/* Close Button */}
              <Box
                w="16px"
                h="16px"
                bg="#C0C0C0"
                border="2px outset white"
                textAlign="center"
                lineHeight="12px"
                fontSize="10px"
                cursor="pointer"
                onClick={handleClose}
                _hover={{ bg: "#FF0000", color: "white" }}
                _active={{ border: "2px inset #808080" }}
                title="Close"
              >
                ✕
              </Box>
            </Flex>
          )}
        </Flex>

        {/* Window Content */}
        <Collapse in={!isMinimized} animateOpacity>
          {children}
        </Collapse>

        {/* Minimized State */}
        {isMinimized && (
          <Flex
            bg="#C0C0C0"
            p={2}
            justify="center"
            align="center"
            borderTop="1px solid #808080"
          >
            <Text
              fontFamily="'Comic Sans MS', cursive"
              fontSize="sm"
              color="black"
            >
              💤 Window minimized - click [_] to restore
            </Text>
          </Flex>
        )}
      </Box>

      {/* Close Confirmation Dialog */}
      {showCloseDialog && (
        <Portal>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0,0,0,0.5)"
            zIndex={2000}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              bg="#C0C0C0"
              border="3px outset white"
              boxShadow="4px 4px 0px #404040"
              minW="300px"
            >
              <Flex
                bg="linear-gradient(90deg, #000080, #1084d0)"
                px={2}
                py={1}
                align="center"
              >
                <Text
                  fontFamily="'Comic Sans MS', cursive"
                  fontWeight="bold"
                  fontSize="sm"
                  color="white"
                >
                  ⚠️ Warning
                </Text>
              </Flex>
              <Box p={4}>
                <Flex align="center" gap={3} mb={4}>
                  <Text fontSize="3xl">⚠️</Text>
                  <Text fontFamily="'Comic Sans MS', cursive" color="black">
                    Are you sure you want to close this window?
                    <br />
                    <Text as="span" fontSize="xs" color="gray.600">
                      (Something unexpected might happen...)
                    </Text>
                  </Text>
                </Flex>
                <Flex justify="center" gap={2}>
                  <Box
                    as="button"
                    bg="#C0C0C0"
                    color="black"
                    border="2px outset white"
                    px={4}
                    py={1}
                    fontFamily="'Comic Sans MS', cursive"
                    fontWeight="bold"
                    cursor="pointer"
                    _hover={{ bg: "#A0A0A0" }}
                    _active={{ border: "2px inset #808080" }}
                    onClick={confirmClose}
                  >
                    Yes
                  </Box>
                  <Box
                    as="button"
                    bg="#C0C0C0"
                    color="black"
                    border="2px outset white"
                    px={4}
                    py={1}
                    fontFamily="'Comic Sans MS', cursive"
                    fontWeight="bold"
                    cursor="pointer"
                    _hover={{ bg: "#A0A0A0" }}
                    _active={{ border: "2px inset #808080" }}
                    onClick={() => setShowCloseDialog(false)}
                  >
                    No
                  </Box>
                </Flex>
              </Box>
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
};
